import { useState } from "react";
import { Home as HomeIcon, Wrench, Building, FileCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  {
    id: "homeowners",
    icon: HomeIcon,
    label: "Homeowners",
    headline: "When your home becomes a project, we make it human again.",
    body: "After a loss, you're balancing insurance calls, contractor schedules and the emotional weight of what just happened. We step in as your contents partner. We document, pack, store and return your belongings so you can focus on your family while we focus on the boxes.",
    bullets: [
      "Empathetic, transparent communication at every stage",
      "Careful pack-out of heirlooms, electronics, art and clothing",
      "Pack-back that resets your home the way it should feel",
    ],
  },
  {
    id: "contractors",
    icon: Wrench,
    label: "Restoration Contractors",
    headline: "A contents partner that doesn't slow your job down.",
    body: "We integrate cleanly with your mitigation, demolition and reconstruction schedules. Same-day pack-outs. Clear chain-of-custody. Inventory documentation your adjusters won't have to chase.",
    bullets: [
      "Coordinated dispatch alongside your mitigation crew",
      "Documented, claims-ready inventory hand-offs",
      "Storage and pack-back synced to your reconstruction milestones",
    ],
  },
  {
    id: "managers",
    icon: Building,
    label: "Property Managers",
    headline: "One coordinated response across your portfolio.",
    body: "From single units to multi-floor buildings, we handle the contents response while you keep tenants, owners and trades aligned. A single point of contact, predictable execution and minimal disruption to operations.",
    bullets: [
      "Multi-unit coordination across complex buildings",
      "Tenant-friendly communication and scheduling",
      "Reporting your ownership group can actually read",
    ],
  },
  {
    id: "insurance",
    icon: FileCheck,
    label: "Insurance Professionals",
    headline: "Inventory that holds up to scrutiny.",
    body: "Detailed, claims-ready documentation built for adjusters. Photographs, categorization, salvageability assessment and a clean chain of custody, so files close cleaner and faster.",
    bullets: [
      "Room-by-room photographic inventory",
      "Salvageable / non-salvageable categorization",
      "Streamlined hand-off to adjusters and TPA platforms",
    ],
  },
];

export default function AudienceTabs() {
  const [active, setActive] = useState(TABS[0].id);
  const tab = TABS.find((t) => t.id === active);
  const Icon = tab.icon;

  return (
    <div data-testid="audience-tabs">
      <div className="flex flex-wrap gap-2 mb-12" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active === t.id}
            onClick={() => setActive(t.id)}
            className={`tab-pill ${active === t.id ? "active" : ""}`}
            data-testid={`audience-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-12 gap-12 items-start" key={tab.id}>
        <div className="md:col-span-5">
          <div className="w-14 h-14 grid place-items-center bg-[hsl(var(--gaza-crimson))] text-white">
            <Icon className="w-6 h-6" strokeWidth={2} />
          </div>
          <h3 className="display-chunky text-4xl md:text-5xl text-[hsl(var(--gaza-teal))] mt-8 leading-[0.95]">{tab.headline}</h3>
        </div>
        <div className="md:col-span-7">
          <p className="text-lg leading-relaxed text-[hsl(var(--gaza-teal-mist))]">{tab.body}</p>
          <ul className="mt-8 space-y-4">
            {tab.bullets.map((b) => (
              <li key={b} className="flex gap-3 text-[hsl(var(--gaza-teal))] font-medium">
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--gaza-crimson))] mt-2.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link to="/contact" className="mt-10 inline-flex items-center gap-2 font-extrabold uppercase tracking-[0.18em] text-sm text-[hsl(var(--gaza-crimson))] link-underline" data-testid={`audience-cta-${tab.id}`}>
            Talk to our team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
